import type { TranslationShape } from "./ro";

export const en: TranslationShape = {
  nav: {
    aboutUs: "About Us",
    services: "Services",
    projects: "Our Projects",
    contact: "Contact",
    callNow: "Call Now",
  },

  footer: {
    description: "Large format printing, outdoor advertising, and professional visual solutions for your business.",
    navigation: "Navigation",
    contact: "Contact",
    cookiePolicy: "Cookie Policy",
    privacy: "Privacy",
    adminAccess: "Admin access",
    rights: "All rights reserved.",
  },

  hero: {
    seeServices: "See services",
    requestQuote: "Request a quote",
    heroTitleText: "Large Format Prints",
    heroSubText: "One image is worth a thousand words. We make thousands of images!",
  },

  about: {
    subtitle: "Learn about us",
    title: "Euro Nova – Your Trusted Partner in",
    titleHighlight: "Large Format",
    p1: "Euro Nova is a Romanian company specializing in large format advertising printing and the production of promotional materials in photo-quality polychrome. With years of experience in the printing and advertising industry, we have become a reference partner for advertising agencies, companies in retail, construction, real estate, automotive, events, and any business that values the visual impact of brand communication.",
    p2_start: "We stand out in the Romanian large format printing market thanks to ",
    p2_bold: "four key pillars: high quality, competitive prices, fast turnaround times, and a dedicated, solution-oriented team.",
    p2_end: " Whether you are an advertising agency with complex briefs or a company that needs advertising banners, mesh banners, large posters, or visual communication materials for points of sale, at Euro Nova, no order is too small or too large.",
    p3_start: "Our production capacity reaches up to ",
    p3_bold: "1,000 square meters per day",
    p3_end: ", supported by state-of-the-art equipment that guarantees high-quality advertising prints and true colors every time. This modern infrastructure allows us to fulfill urgent orders without compromising on final quality—an essential advantage for both agencies working at a fast pace and companies launching campaigns on tight deadlines.",
    p4_start: "The Euro Nova team is made up of young, well-trained professionals with solid experience in advertising production and customer relations. We know what a tight deadline means and treat every project with the same seriousness and attention to detail. More than just execution, we offer ",
    p4_bold: "consulting and complete solutions",
    p4_end: " tailored to each client's specific needs.",
    p5: "Our ever-growing portfolio and recognition as one of Romania's top large-format print producers are proof of the standards we work to every day.",
    p6: "Quality. Speed. Fair prices. Choose Euro Nova — the advertising print producer that lives up to your expectations.",
    videoCaption1: "Large prints - big clients!",
    videoCaption2: "Large format printing at photo quality — our equipment in operation.",
  },

  cta: {
    title: "Have a project",
    titleHighlight: "in mind",
    description: "Contact us for a personalized quote. We deliver fast, at the best prices.",
    button: "Request a quote",
  },

  services: {
    subtitle: "Services",
    title: "Our Services",
    description: "We offer a complete range of large format printing services, from concept to finished product.",
    items: [
      {
        title: "Banners & Mesh",
        description: "Printing on PVC banner and large-format mesh, ideal for outdoor advertising, events, construction, and festivals. UV and weather resistant, with vibrant and durable colors.",
        features: ["Width up to 3.2m", "UV resistance", "Eyelet finishing"],
      },
      {
        title: "Stickers & Films",
        description: "PVC stickers, cast and polymeric films for vehicle wrapping, window decoration, custom wallpapers, and long-lasting indoor/outdoor applications.",
        features: ["Vehicle films", "Window films", "Protective lamination"],
      },
      {
        title: "Posters & Canvas",
        description: "High-resolution photographic printing on premium paper, artist canvas, and backlit paper for light boxes. Faithful colors and exceptional details.",
        features: ["1440dpi resolution", "Premium paper", "Framed canvas"],
      },
      {
        title: "Labels & Digital/Offset Print",
        description: "Polychrome PVC labels resistant to water and UV, straight or with special shapes. Digital and offset printing (business cards, flyers, catalogs), posters in small/unique series or large runs.",
        features: ["PVC labels", "Offset printing", "Large runs"],
      },
      {
        title: "Signage",
        description: "Illuminated and non-illuminated volumetric letters, light boxes, advertising panels, totems, and interior and exterior signage systems.",
        features: ["Volumetric letters", "Light boxes", "Totems"],
      },
      {
        title: "Graphic Design",
        description: "Complete graphic design services: file adaptation for print, creation of advertising layouts, brandbook, visual identity, and file preparation.",
        features: ["Print layouts", "Visual identity", "File preparation"],
      },
    ],
    materialsSubtitle: "Materials",
    materialsTitle: "What we print on",
    outdoorTitle: "Outdoor Materials",
    outdoorDesc: "PVC tarpaulin (single and double-sided), mesh, backlit tarpaulin (light box), PVC sticker (panels, exterior windows, vehicle lettering), window graphic (one-way vision material for shop windows), flags for poles / beach flag",
    indoorTitle: "Indoor Materials",
    indoorDesc: "Photo paper – posters, PVC sticker, canvas, polypropylene – RollUp, backlit film, display fabric (popUP, sampling table)",
  },

  projects: {
    subtitle: "Portfolio",
    title: "Our Projects",
    description: "Discover a selection of works completed by the Euro Nova team.",
    items: [
      "Store facade setup",
      "Double-sided light box, indoor display",
      "Large format banners",
      "Custom-shaped PVC labels",
      "Canvas paintings - interior design",
      "Vehicle wrapping",
      "Mesh - building facade coverings",
      "Textile wallpaper - single image, no tiles",
      "Truck tarpaulin print",
      "Exhibition stand setup",
    ],
    prevAria: "Previous project",
    nextAria: "Next project",
    goToAria: "Go to project",
  },

  contact: {
    subtitle: "Contact",
    title: "Contact Us",
    description: "Request a quote or ask us any question about our printing services.",
    info: "Information",
    address: "Address",
    phone: "Phone",
    email: "Email",
    schedule: "Schedule",
    scheduleValue: "Monday – Friday: 09:00 – 16:00",
    sendFiles: "Send your files",
    sendFilesDesc: "You can send your print files to",
    acceptFormats: ". We accept formats: PDF, AI, EPS, TIFF, PSD, JPG (min. 150 DPI).",
    formTitle: "Request a quote",
    name: "Name",
    namePlaceholder: "Your name",
    emailLabel: "Email",
    emailPlaceholder: "email@example.com",
    phoneLabel: "Phone",
    phonePlaceholder: "+40 7xx xxx xxx",
    message: "Message / Project details",
    messagePlaceholder: "Describe your project: dimensions, material, quantity...",
    attachFiles: "Attach files",
    clickToAdd: "Click to add files",
    submit: "Send request",
    submitting: "Sending...",
    successTitle: "Request sent!",
    successDesc: "We will contact you as soon as possible.",
    sendAnother: "Send another request",
    rateLimitError: "You have sent too many requests. Please try again later.",
    nameError: "Name must be between 2 and 100 characters.",
    emailError: "Email address is not valid.",
    phoneError: "Phone number is not valid.",
    messageError: "Message must be between 10 and 2000 characters.",
    maxFilesError: "You can attach a maximum of",
    fileSizeError: "exceeds the 20MB limit.",
    files: "files",
  },

  notFound: {
    title: "404",
    message: "Oops! Page not found",
    backHome: "Return to home page",
  },
};
